import { useMemo } from 'react';
import * as yup from 'yup';
import { FORM_LINE_TYPES } from 'src/components/ui/FormLine/constants';
import {
	COMMISSION_PRIORITY_OPTIONS,
	SALES_TARGET_OPTIONS,
	isCommissionSalesTarget,
} from 'src/utils/mappers/companySalesTarget.mapper';
import {
	buildMatchOptions,
	buildRateOptions,
	buildWorthOptions,
	matchOptions,
	rateOptions,
	worthOptions,
} from './worthOptions';

export { rateOptions, worthOptions, matchOptions } from './worthOptions';

export const useFormWorth = (params = {}) => {
	const { values, options: optionsProp, withSalesTarget = false } = params;

	const fieldOptions = useMemo(
		() => ({
			worth: optionsProp?.worth ?? worthOptions,
			wedding: optionsProp?.wedding ?? rateOptions,
			birthday: optionsProp?.birthday ?? rateOptions,
			corporate: optionsProp?.corporate ?? matchOptions,
			newYear: optionsProp?.newYear ?? rateOptions,
			graduation: optionsProp?.graduation ?? matchOptions,
		}),
		[optionsProp],
	);

	const initialValues = useMemo(() => {
		return {
			worth: null,
			wedding: null,
			birthday: null,
			corporate: null,
			newYear: null,
			graduation: null,
			...(withSalesTarget
				? {
					salesTarget: null,
					commissionPriority: null,
				}
				: {}),
			...values,
		};
	}, [values, withSalesTarget]);

	const showPriority =
		withSalesTarget && isCommissionSalesTarget(initialValues.salesTarget);

	const shape = useMemo(() => {
		const lines = [
			{
				id: 1,
				field: {
					type: FORM_LINE_TYPES.autocomplete,
					connected: true,
					params: {
						name: 'worth',
						label: 'Ценность',
						options: fieldOptions.worth,
					},
				},
			},
			{
				id: 2,
				field: {
					type: FORM_LINE_TYPES.autocomplete,
					connected: true,
					params: {
						name: 'wedding',
						label: 'Свадьба',
						options: fieldOptions.wedding,
					},
				},
			},
			{
				id: 3,
				field: {
					type: FORM_LINE_TYPES.autocomplete,
					connected: true,
					params: {
						name: 'birthday',
						label: 'День рождения',
						options: fieldOptions.birthday,
					},
				},
			},
			{
				id: 4,
				field: {
					type: FORM_LINE_TYPES.autocomplete,
					connected: true,
					params: {
						name: 'corporate',
						label: 'Корпоратив',
						selectOnly: true,
						options: fieldOptions.corporate,
					},
				},
			},
			{
				id: 5,
				field: {
					type: FORM_LINE_TYPES.autocomplete,
					connected: true,
					params: {
						name: 'newYear',
						label: 'Новый год',
						options: fieldOptions.newYear,
					},
				},
			},
			{
				id: 6,
				field: {
					type: FORM_LINE_TYPES.autocomplete,
					connected: true,
					params: {
						name: 'graduation',
						label: 'Выпускной',
						selectOnly: true,
						options: fieldOptions.graduation,
					},
				},
			},
		];

		if (withSalesTarget) {
			lines.push({
				id: 7,
				field: {
					type: FORM_LINE_TYPES.autocomplete,
					connected: true,
					params: {
						name: 'salesTarget',
						label: 'Цель продажи',
						placeholder: 'Выберите',
						selectOnly: true,
						options: SALES_TARGET_OPTIONS,
					},
				},
			});

			if (showPriority) {
				lines.push({
					id: 8,
					field: {
						type: FORM_LINE_TYPES.autocomplete,
						connected: true,
						params: {
							name: 'commissionPriority',
							label: 'Приоритет комиссии',
							placeholder: 'Выберите',
							selectOnly: true,
							options: COMMISSION_PRIORITY_OPTIONS,
						},
					},
				});
			}
		}

		return {
			groups: [
				{
					id: 1,
					lines,
				},
			],
		};
	}, [fieldOptions, withSalesTarget, showPriority]);

	const validationSchema = useMemo(
		() =>
			yup.object().shape({
				worth: yup.object().required('Укажите ценность'),
				wedding: yup.object().required('Укажите оценку'),
				birthday: yup.object().required('Укажите оценку'),
				corporate: yup.object().required('Укажите оценку'),
				newYear: yup.object().required('Укажите оценку'),
				graduation: yup.object().required('Укажите оценку'),
				...(withSalesTarget
					? {
						salesTarget: yup.object().nullable(),
						commissionPriority: yup.object().nullable(),
					}
					: {}),
			}),
		[withSalesTarget],
	);

	return { shape, initialValues, validationSchema };
};

// keep helpers re-exported for stories/tests that may import from here
export { buildRateOptions, buildWorthOptions, buildMatchOptions };
